import React, { useState, useEffect, useCallback } from 'react';
import { FileDown } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalFooter } from '../shared/Modal';
import { useTheme } from '../../contexts/ThemeContext';

export interface PDFExportSections {
  netWorth: boolean;
  banking: boolean;
  creditCards: boolean;
  hsa: boolean;
  investments: boolean;
  retirement401k: boolean;
  crypto: boolean;
  privateEquity: boolean;
  assets: boolean;
  liabilities: boolean;
  budget: boolean;
  goals: boolean;
  charts: boolean;
}

const STORAGE_KEY = 'tallyup_pdf_sections';

const DEFAULT_SECTIONS: PDFExportSections = {
  netWorth: true,
  banking: true,
  creditCards: true,
  hsa: true,
  investments: true,
  retirement401k: true,
  crypto: true,
  privateEquity: true,
  assets: true,
  liabilities: true,
  budget: true,
  goals: true,
  charts: true,
};

const SECTION_LABELS: Record<keyof PDFExportSections, string> = {
  netWorth: 'Net Worth Summary',
  banking: 'Banking Accounts',
  creditCards: 'Credit Cards',
  hsa: 'HSA Accounts',
  investments: 'Investments',
  retirement401k: '401k Retirement',
  crypto: 'Crypto Holdings',
  privateEquity: 'Private Equity',
  assets: 'Assets',
  liabilities: 'Liabilities',
  budget: 'Monthly Budget',
  goals: 'Financial Goals',
  charts: 'Pro Charts & Analysis',
};

interface PDFExportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onExport: (sections: PDFExportSections) => void;
}

function loadSections(): PDFExportSections {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...DEFAULT_SECTIONS, ...parsed };
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_SECTIONS };
}

export const PDFExportModal: React.FC<PDFExportModalProps> = ({ isOpen, onClose, onExport }) => {
  const { darkMode } = useTheme();
  const [sections, setSections] = useState<PDFExportSections>(loadSections);

  // Re-load from localStorage each time the modal opens
  useEffect(() => {
    if (isOpen) {
      setSections(loadSections());
    }
  }, [isOpen]);

  const persist = useCallback((next: PDFExportSections) => {
    setSections(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }, []);

  const toggle = (key: keyof PDFExportSections) => {
    persist({ ...sections, [key]: !sections[key] });
  };

  const allSelected = (Object.keys(sections) as Array<keyof PDFExportSections>).every(k => sections[k]);
  const noneSelected = (Object.keys(sections) as Array<keyof PDFExportSections>).every(k => !sections[k]);

  const selectAll = () => {
    const next = { ...sections };
    for (const key of Object.keys(next) as Array<keyof PDFExportSections>) {
      next[key] = true;
    }
    persist(next);
  };

  const deselectAll = () => {
    const next = { ...sections };
    for (const key of Object.keys(next) as Array<keyof PDFExportSections>) {
      next[key] = false;
    }
    persist(next);
  };

  const handleExport = () => {
    onExport(sections);
    onClose();
  };

  const sectionKeys = Object.keys(SECTION_LABELS) as Array<keyof PDFExportSections>;

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="md">
      <ModalContent>
        <ModalHeader
          title="Export PDF"
          icon={
            <div className={`mx-auto w-14 h-14 rounded-full flex items-center justify-center ${
              darkMode ? 'bg-violet-500/20' : 'bg-violet-100'
            }`}>
              <FileDown size={28} className={darkMode ? 'text-violet-400' : 'text-violet-600'} />
            </div>
          }
        />

        <p className={`text-sm text-center mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
          Choose which sections to include in your PDF report.
        </p>

        {/* Select All / Deselect All */}
        <div className="flex justify-end mb-3">
          <button
            onClick={allSelected ? deselectAll : selectAll}
            className={`text-xs font-medium transition-colors ${
              darkMode ? 'text-violet-400 hover:text-violet-300' : 'text-violet-600 hover:text-violet-700'
            }`}
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>

        {/* Two-column checkbox grid */}
        <div className="grid grid-cols-2 gap-2">
          {sectionKeys.map((key) => (
            <label
              key={key}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                darkMode
                  ? sections[key] ? 'bg-violet-500/10 border border-violet-500/20' : 'bg-white/5 border border-white/10'
                  : sections[key] ? 'bg-violet-50 border border-violet-200' : 'bg-slate-50 border border-slate-200'
              }`}
            >
              <input
                type="checkbox"
                checked={sections[key]}
                onChange={() => toggle(key)}
                className="w-4 h-4 rounded accent-purple-600"
              />
              <span className={`text-xs font-medium ${
                darkMode ? 'text-slate-200' : 'text-slate-700'
              }`}>
                {SECTION_LABELS[key]}
              </span>
            </label>
          ))}
        </div>

        <ModalFooter>
          <button
            onClick={onClose}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm transition-colors ${
              darkMode
                ? 'bg-white/10 hover:bg-white/20 text-white'
                : 'bg-slate-100 hover:bg-slate-200 text-slate-800'
            }`}
          >
            Cancel
          </button>
          <button
            onClick={handleExport}
            disabled={noneSelected}
            className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm text-white transition-colors ${
              noneSelected
                ? 'bg-violet-500/50 cursor-not-allowed'
                : 'bg-violet-600 hover:bg-violet-700'
            }`}
          >
            Export PDF
          </button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
